import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
// Self-hosted webfonts (was Google Fonts CDN). The @fontsource
// packages ship woff2 + per-weight @font-face declarations the
// Vite asset pipeline picks up + emits with content-hashed
// filenames so the SW precache catches them on every install.
// Privacy + offline + Save-Data both gain: no third-party round-
// trip, the cold-cache install is fully local, and the fonts
// stay available when the user is offline. Imported weights
// mirror what the skin's type scale asks for (Nunito 400 / 500
// / 600 / 700 / 800 + JetBrains Mono 400 / 500 / 700 / 800).
//
// Nunito, not Inter: the soft-3D direction rests on a ROUNDED
// grotesque. Inter's flat terminals read as a different product
// beside the same shadows and corners, so the face is part of
// the design decision, not an interchangeable default.
//
// LATIN-ONLY subset: SUPPORTED_LOCALES = ko-KR + en. The English UI
// is plain ASCII (latin); Nunito carries no Hangul, so Korean renders
// through the OS face (-apple-system / system-ui — see theme tokens).
// That is a DELIBERATE choice, not a gap: a bundled rounded Hangul
// face was tried and the platform face read better, at ~260 KB less.
//
// The per-weight barrels (`@fontsource/nunito/400.css`) pull several
// subsets each (cyrillic / vietnamese / latin*) — all but latin are
// dead weight here. The `latin-*` variant emits only the latin subset.
import "@fontsource/nunito/latin-400.css";
import "@fontsource/nunito/latin-500.css";
import "@fontsource/nunito/latin-600.css";
import "@fontsource/nunito/latin-700.css";
import "@fontsource/nunito/latin-800.css";
import "@fontsource/jetbrains-mono/latin-400.css";
import "@fontsource/jetbrains-mono/latin-500.css";
import "@fontsource/jetbrains-mono/latin-700.css";
import "@fontsource/jetbrains-mono/latin-800.css";
import { App } from "./App";
import { DEFAULT_SKIN } from "./theme/skins/default";
import { applyTokens } from "./theme/ThemeProvider";
import { useAuthStore } from "./auth/auth_store";
import { restoreSession as restoreSigningKeySession } from "./auth/signing_key_idb";
import { preloadMlDsa } from "./crypto/ml_dsa";
import { fetchWalletSnapshot } from "./session/wallet_sync";
// i18n bootstrap — initialise BEFORE rendering so
// the first paint already runs through the negotiated locale; the
// init is idempotent so a future runtime switch via `setLocale`
// just swaps the active language without re-init. The boot read
// has no `accountLocale` yet (login hasn't landed), so the chain
// falls through localStorage → navigator → ko-KR fallback.
import { i18next, initI18n } from "./i18n";
import { requestPersistentStorage } from "./lib/persistence";
import { installSoundUnlock } from "./lib/sound";
initI18n();
// Every cue fires from a socket frame, so the audio has to be started
// by a touch before the first one arrives (`installAudioUnlock`).
installSoundUnlock();
// Fire-and-forget persistence request. Promotes the origin's
// storage tier from "best effort" to "persistent" so the SW
// precache + the auth-store localStorage don't get evicted
// under device-wide storage pressure (Chrome / Firefox / Safari
// 17+ semantics). Logs the verdict via `console.debug` only —
// no UI surface today; a future quota-warning panel can read
// the same `requestPersistentStorage()` probe.
void requestPersistentStorage().then((probe) => {
  console.debug("[arkhe-holdem] storage persistence probe", probe);
});
// Expose the singleton on `globalThis.__arkheI18n` so the legacy
// `lib/server_error.ts` resolver can reach `t()` without importing
// the i18n module directly — avoids the import cycle between the
// server-error helper (used by every fetch wrapper) and the
// i18n setup (which eventually depends on bundled assets).
(globalThis as unknown as { __arkheI18n: typeof i18next }).__arkheI18n =
  i18next;

const rootEl = document.getElementById("root");
if (rootEl === null) {
  throw new Error("#root element missing from index.html");
}

// Async boot rehydrate — attempt to restore the prior session
// from the encrypted IndexedDB row before the AuthGate mounts so
// a PWA-update reload (or any plain refresh) lands directly in
// the lobby instead of bouncing to the login screen. The decrypt
// runs in parallel with React paint scheduling: we kick the IDB
// open here and hydrate the auth slice as soon as it resolves;
// the brief blank frame between paint + hydrate is shorter than
// the login screen flash a synchronous wait would cost. A miss
// (no row / private mode / decrypt fail) leaves the slice in
// `phase === "logged_out"` so the LoginScreen renders normally.
void restoreSigningKeySession().then(async (restored) => {
  if (restored === null) return;
  // A hybrid account restores a non-null ml-dsa seed — the ONLY transition
  // that sets one (login() always seeds null genesis). Preload the lazy
  // `@noble/post-quantum` chunk BEFORE the store-set so the seed only
  // becomes visible to any synchronous signer AFTER the module is cached;
  // this eliminates the race where a sign path fires from a React effect
  // before the preload resolves. An ed25519-only account skips this
  // entirely, so the post-quantum chunk never loads on the live path. The
  // restore itself stays off the render-blocking path (createRoot.render
  // below runs concurrently), preserving the no-login-flash boot.
  if (restored.mlDsaSeed != null) {
    await preloadMlDsa();
  }
  // Restore FIRST so the auth slice carries the credential legs
  // (`ed25519Epoch` / ml-dsa) the boot probe needs to sign correctly
  // for a rotated / hybrid account; `restoreSession` arms
  // `bootProbePending`, which holds the lobby behind a neutral
  // loading shell (AuthGateContent) until the probe resolves.
  useAuthStore.getState().restoreSession(restored);
  // Boot session-validity probe. The server sweeps idle (30 m) /
  // absolute (8 h) sessions, so a refresh-after-idle restores a
  // cookie the BE has already revoked. Fire ONE cheap authed read
  // (`/accounts/me/sync`, which rides the session cookie + the
  // account-self signature) BEFORE the lobby paints:
  //   - 200            → session live. Seed the freshly-probed wallet
  //                      + clear the gate so the lobby mounts with the
  //                      real chip balance (no "$0" flash).
  //   - session 401    → `authedRequest` already funnelled the
  //                      canonical revoke reason through
  //                      `markSessionRevoked` → `phase logged_out`, so
  //                      the AuthGate falls through to the LoginScreen.
  //                      Leave `bootProbePending` as-is (the logout
  //                      reset cleared it) and do NOT mount the lobby.
  //   - transient miss → offline / 5xx / network. Fail OPEN: clear the
  //                      gate so the lobby mounts against the restored
  //                      (last-known) state and its own reads retry.
  //                      A genuinely-revoked session that slipped a
  //                      transient probe still logs out on the lobby's
  //                      first authed read via the same cascade.
  const { serverBase, playerIdHex, signingKey } = useAuthStore.getState();
  if (playerIdHex !== null && signingKey !== null) {
    try {
      const snapshot = await fetchWalletSnapshot({
        serverBase,
        playerIdHex,
        signingKey,
      });
      useAuthStore.getState().clearBootProbe({
        chipFree: snapshot.free,
        chipInPlay: snapshot.inPlay,
        chipHoldTotal: snapshot.held,
      });
    } catch {
      // Session-class 401 already logged out via the authedRequest
      // cascade; if we are still in the lobby phase the failure was
      // transient — release the gate so the lobby mounts.
      if (useAuthStore.getState().phase === "lobby") {
        useAuthStore.getState().clearBootProbe();
      }
    }
  } else {
    // Defensive — a restored session always carries both, but never
    // wedge the gate shut if a malformed row slipped through.
    useAuthStore.getState().clearBootProbe();
  }
});

// Tokens before the first paint — the skin stylesheet resolves every
// colour, font and radius through `var(--token)` with no literal
// fallback, so the document root has to carry them before React
// paints anything.
applyTokens(DEFAULT_SKIN);

createRoot(rootEl).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
